import { format } from '../../utils/utils';
import {
  ErrorMessage,
  ObjectSchema,
  Schema,
  isArraySchema,
  isBooleanSchema,
  isIntegerSchema,
  isNullSchema,
  isNumberSchema,
  isObjectSchema,
  isStringSchema
} from '../types';
import { evaluateSubschema } from './evaluateSchema';

let globalId = 0;
export const guid = () => ++globalId;
guid.reset = () => {
  globalId = 0;
};

export function getValueFromInputTarget(
  target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
) {
  return target.type === 'checkbox'
    ? (target as HTMLInputElement).checked
    : target.value;
}

/**
 * Return a sensible default value for a given JSON Schema `schema`.
 *
 * If the schema has an explicitly defined `default` value, it will use that.
 *
 * @param {Schema} schema
 */
function getDefaultSchemaValue(schema: Schema) {
  if (Object.prototype.hasOwnProperty.call(schema, 'default')) {
    return schema.default;
  }

  if (isObjectSchema(schema)) {
    return {};
  }

  if (isStringSchema(schema)) {
    return '';
  }

  if (isBooleanSchema(schema)) {
    return null;
  }

  if (isNumberSchema(schema)) {
    return null;
  }

  if (isIntegerSchema(schema)) {
    return null;
  }

  if (isArraySchema(schema)) {
    return [];
  }

  if (isNullSchema(schema)) {
    return null;
  }

  throw new Error(
    `getDefaultSchemaValue invalid schema ${JSON.stringify(schema)}`
  );
}

/**
 * Build a data-structure containing values defined by the given `schema`,
 * based on the `initialValue`.
 *
 * It recursively goes through the schema and applies default values
 * where values are missing, or replacing them with a constant value
 * if the JSON schema is defined as a `const`.
 *
 * Unlike `removeRedundantValues`, this function will *not discard* values
 * in `initialValue` that is not part of the schema definition. By keeping
 * the values we allow the user to accidentally change the value at a branching
 * point and undo it _without_ losing their input.
 *
 * This function should only be applied on schemas where branches
 * already have been evaluated.
 *
 * @see removeRedundantValues
 * @param {Schema} schema - The JSON Schema
 * @param {mixed} initialValue - The initial value
 */
export function getSchemaValues(schema: Schema, initialValue: any) {
  if (isObjectSchema(schema)) {
    const properties = schema.properties || {};
    return {
      ...(initialValue || getDefaultSchemaValue(schema)),
      ...Object.keys(properties).reduce((values: any, propertyName) => {
        values[propertyName] = getSchemaValues(
          properties[propertyName],
          (initialValue || {})[propertyName]
        );
        return values;
      }, {})
    };
  }

  if (isArraySchema(schema)) {
    if (isObjectSchema(schema.items)) {
      return (initialValue || getDefaultSchemaValue(schema)).map(
        (value: any) => {
          const updated = value || getDefaultSchemaValue(schema.items);
          return {
            ...updated,
            _id: updated._id == null ? guid() : updated._id,
            ...getSchemaValues(
              evaluateSubschema(schema.items, updated) as ObjectSchema,
              updated
            )
          };
        }
      );
    }

    return (initialValue || getDefaultSchemaValue(schema)).map((value: any) =>
      getSchemaValues(schema.items, value)
    );
  }

  if (Object.prototype.hasOwnProperty.call(schema, 'const')) {
    return schema.const;
  }

  // Both `null` and `undefined` should trigger default value
  return initialValue == null ? getDefaultSchemaValue(schema) : initialValue;
}

/**
 * `zip` takes two arrays and return a new array of tuples
 * consisting of one item from each with the length equal to
 * the length of the first array.
 *
 * If the first array contains more elements than the second,
 * the extra values will be `undefined`.
 *
 * ```
 * const enumNames = ['a', 'b'];
 * const enumValues = [1, 2]
 * const result = zip(enumNames, enumValues)
 *
 * assert(result === [['a', 1], ['b', 2]])
 * ```
 */
export function zip<A, B>(
  a: Array<A>,
  b: Array<B> = []
): Array<[A, B | undefined]> {
  const result: Array<[A, B | undefined]> = [];
  for (let i = 0; i < a.length; i++) {
    result.push([a[i], b[i]]);
  }
  return result;
}

/**
 * Convert a ErrorMessage structure to a string, with error
 * keys mapped to one of the provided texts. `texts` could
 * come from FMS.
 */
export function joinErrors(
  errors: Array<ErrorMessage>,
  getText: (key: string) => string
) {
  return errors
    .map((error: any) => {
      if (error.message) {
        return format(getText(error.message) || error.message, error.args);
      }

      return getText(error) || error;
    })
    .join(', ');
}

/**
 * Best-effort ordering of properties based on
 * a given order.
 *
 * Properties not defined in the order array
 * will be put after the ones that have a defined order.
 */
export function orderProperties(
  properties: Array<string>,
  order: Array<string> | null | undefined
) {
  if (!Array.isArray(order)) {
    return properties;
  }

  const orderSet = new Set(order);
  const propertySet = new Set(properties);

  const result = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const property of order) {
    if (!propertySet.has(property)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    result.push(property);
  }

  const rest = properties.filter(property => !orderSet.has(property));
  return result.concat(rest);
}

export function concatUnique<T>(
  xs: Array<T> | null | undefined,
  ys: Array<T> | null | undefined
) {
  return [...new Set((xs || []).concat(ys || []))];
}

function removeRequiredProperty(
  required: Array<string> | undefined,
  propertyName: string
): void {
  if (required) {
    const index = required.indexOf(propertyName);
    if (index > -1) {
      required.splice(index, 1);
    }
  }
}

function removeSchemaPropertyByPath(
  baseSchema: ObjectSchema,
  propertyPath: string
): void {
  const propertyPathSteps = propertyPath.split('.');
  let schema = baseSchema;
  let { properties } = baseSchema;

  // Traverse object until end of given path and delete that property, or until a non object schema is hit (faulty path)
  // eslint-disable-next-line no-restricted-syntax
  for (const [index, pathStep] of propertyPathSteps.entries()) {
    if (properties && Object.keys(properties).includes(pathStep)) {
      if (index === propertyPathSteps.length - 1) {
        delete properties[pathStep];
        removeRequiredProperty(schema.required, pathStep);
        return;
      }
      const propertySchema = properties[pathStep];
      if (
        propertySchema &&
        isObjectSchema(propertySchema) &&
        propertySchema.properties
      ) {
        properties = propertySchema.properties;
        schema = propertySchema;
      } else {
        break;
      }
    }
  }
}

/**
 * Remove all instances of property definitions for a given property from the given object schema.
 * Also removes its "required" reference.
 */
export function removeInstancesOfSchemaProperty(
  schema: ObjectSchema,
  propertyPath: string
): void {
  if (schema.properties) {
    removeSchemaPropertyByPath(schema, propertyPath);
  }
  if (schema.then?.properties) {
    removeSchemaPropertyByPath(schema.then, propertyPath);
  }
  if (schema.else?.properties) {
    removeSchemaPropertyByPath(schema.else, propertyPath);
  }
  if (schema.allOf) {
    Object.values(schema.allOf).forEach(allOfSchema =>
      removeInstancesOfSchemaProperty(allOfSchema, propertyPath)
    );
  }
}
